var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { produce } from 'immer';
import clsx from 'clsx';
import { GetCommonSettings } from '@themes/common/data';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import FilterListIcon from '@mui/icons-material/FilterList';
import Popover from '@mui/material/Popover';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Box from '@mui/material/Box';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled, useTheme } from '@mui/material/styles';
import { SortByLocaleCompare } from '@components/Shared/Utils/sort';
import { Distinct } from '@commons/helpers';
import useMediaQuery from '@mui/material/useMediaQuery';
const StenbolagetTooltip = styled((_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return _jsx(Tooltip, Object.assign({}, props, { arrow: true, classes: { popper: className } }));
})(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.primary.main,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.primary.main,
    },
}));
export default function ProductGalleryFiltersSection({ product_gallery_items, selected_item, onItemChange }) {
    const theme = useTheme();
    const threeColumns = useMediaQuery(theme.breakpoints.between(450, 700));
    const fourColumns = useMediaQuery(theme.breakpoints.between(700, 1050));
    const [search, setSearch] = useState('');
    const [openFilter, setOpenFilter] = useState(false);
    const [filteredFilters, setFilteredFilters] = useState({
        color: [],
        product_type: [],
        style: [],
        texture: [],
        material: [],
    });
    const filterButton = useRef(null);
    const { translations } = GetCommonSettings();
    const searchFilter = useCallback((item) => {
        var _a;
        const FilterValues = (key) => !filteredFilters[key] ||
            filteredFilters[key].length === 0 ||
            (filteredFilters[key].length > 0 && filteredFilters[key].includes(item[key]));
        return ((!search || ((_a = item.title) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes(search.toLowerCase()))) &&
            FilterValues('color') &&
            FilterValues('product_type') &&
            FilterValues('style') &&
            FilterValues('texture') &&
            FilterValues('material'));
    }, [filteredFilters, search]);
    const popular_products = useMemo(() => {
        return product_gallery_items.filter(searchFilter);
    }, [product_gallery_items, searchFilter]);
    const groupedMaterials = useMemo(() => {
        return product_gallery_items.reduce((previousResult, item) => {
            return produce(previousResult, (result) => {
                if (searchFilter(item)) {
                    if (!result[item.material]) {
                        result[item.material] = [item];
                    }
                    else {
                        result[item.material].push(item);
                    }
                }
            });
        }, {});
    }, [product_gallery_items, searchFilter]);
    const materials = useMemo(() => [...Object.keys(groupedMaterials)].sort(), [groupedMaterials]);
    const handleOnSearch = useCallback((e) => {
        var _a;
        setSearch((_a = e.target.value) !== null && _a !== void 0 ? _a : '');
        setFilteredFilters({
            color: [],
            product_type: [],
            style: [],
            texture: [],
        });
    }, []);
    const handleFilterToggle = useCallback(() => setOpenFilter((currentState) => !currentState), []);
    const filters = useMemo(() => {
        const FilterValues = (key) => { var _a; return Distinct((_a = product_gallery_items.map((m) => m[key])) !== null && _a !== void 0 ? _a : []); };
        return {
            color: FilterValues('color'),
            product_type: FilterValues('product_type'),
            style: FilterValues('style'),
            texture: FilterValues('texture'),
            material: FilterValues('material'),
        };
    }, [product_gallery_items]);
    const activeFilters = useMemo(() => {
        const FilterValues = (key) => { var _a; return Distinct((_a = product_gallery_items.filter(searchFilter).map((m) => m[key])) !== null && _a !== void 0 ? _a : []); };
        return {
            color: FilterValues('color'),
            product_type: FilterValues('product_type'),
            style: FilterValues('style'),
            texture: FilterValues('texture'),
            material: FilterValues('material'),
        };
    }, [product_gallery_items, searchFilter]);
    const availableFilters = useMemo(() => {
        const filteredFilters = Object.keys(filters).filter((key) => filters[key].length > 0);
        return filteredFilters.sort(SortByLocaleCompare((key) => translations.product_gallery[key] || key));
    }, [filters, translations.product_gallery]);
    const handleFiltersToggle = useCallback((key, item) => setFilteredFilters(produce((currentFilters) => {
        if (item) {
            const indexOfItem = currentFilters[key].indexOf(item);
            if (indexOfItem !== -1) {
                currentFilters[key].splice(indexOfItem, 1);
            }
            else {
                currentFilters[key].push(item);
            }
        }
        else if (currentFilters[key].length !== filters[key].length) {
            currentFilters[key] = filters[key];
        }
        else {
            currentFilters[key] = [];
        }
    })), [filters]);
    const columns = useMemo(() => (threeColumns ? 3 : fourColumns ? 4 : 2), [threeColumns, fourColumns]);
    return (_jsx("div", Object.assign({ className: 'filter-section' }, { children: _jsxs("div", Object.assign({ className: 'filter-wrapper' }, { children: [_jsxs("div", Object.assign({ className: 'filter-list-wrapper' }, { children: [_jsxs(FormControl, Object.assign({ fullWidth: true, variant: 'outlined', size: 'medium' }, { children: [_jsx(InputLabel, Object.assign({ htmlFor: 'gallery-search' }, { children: translations.product_gallery.search })), _jsx(OutlinedInput, { id: 'gallery-search', type: 'text', endAdornment: _jsx(InputAdornment, Object.assign({ position: 'end' }, { children: _jsx(IconButton, Object.assign({ ref: filterButton, "aria-label": 'toggle password visibility', onClick: handleFilterToggle, edge: 'end' }, { children: _jsx(FilterListIcon, {}) })) })), label: translations.product_gallery.search, value: search, onChange: handleOnSearch })] })), _jsx(Popover, Object.assign({ open: openFilter, anchorEl: openFilter ? filterButton.current : null, onClose: handleFilterToggle, anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'right',
                            }, transformOrigin: {
                                vertical: 'top',
                                horizontal: 'right',
                            }, disableScrollLock: true }, { children: _jsx(Box, Object.assign({ sx: { p: 2 } }, { children: availableFilters.map((filterKey) => (_jsxs(React.Fragment, { children: [_jsx("div", Object.assign({ className: 'sb-product-gallery--filter-heading' }, { children: translations.product_gallery[filterKey] || filterKey })), _jsx(Box, Object.assign({ sx: { display: 'flex', flexDirection: 'column', ml: 3 } }, { children: filters[filterKey]
                                                .sort((filterA, filterB) => filterA.localeCompare(filterB))
                                                .map((item) => {
                                                var _a, _b;
                                                return (_jsx(FormControlLabel, { label: item, disabled: !((_a = activeFilters[filterKey]) === null || _a === void 0 ? void 0 : _a.includes(item)), control: _jsx(Checkbox, { checked: (_b = filteredFilters[filterKey]) === null || _b === void 0 ? void 0 : _b.includes(item), onChange: () => handleFiltersToggle(filterKey, item), size: 'small' }) }, item));
                                            }) }))] }, filterKey))) })) }))] })), popular_products.length === 0 && materials.length === 0 && (_jsx("div", Object.assign({ className: 'filter-container' }, { children: _jsx("h6", Object.assign({ className: 'title' }, { children: translations.search.no_result_found })) }))), materials
                    .sort((materialA, materialB) => materialA.localeCompare(materialB))
                    .map((material) => (_jsxs("div", Object.assign({ className: 'filter-container' }, { children: [_jsx("h6", Object.assign({ className: 'title' }, { children: material })), _jsx("div", Object.assign({ className: clsx('filter-items'), style: {
                                '--filter-items-column-count': columns,
                            } }, { children: groupedMaterials[material]
                                .filter((item) => item)
                                .sort(SortByLocaleCompare((item) => item.title))
                                .map((item) => (_jsx(StenbolagetTooltip, Object.assign({ title: item.title, placement: 'top' }, { children: _jsxs("div", Object.assign({ onClick: () => onItemChange(item.id), "data-background": item.thumbnail, className: clsx('item', {
                                        active: item.id === (selected_item === null || selected_item === void 0 ? void 0 : selected_item.id),
                                    }) }, { children: [_jsx("div", { className: 'item--image image-cover cover-loaded aspect-ratio--1-1 lazy', "data-background": item.thumbnail }), _jsx("div", Object.assign({ className: 'item--title' }, { children: item.title }))] }), item.id) }), item.id))) }))] }), material))), popular_products.length > 0 && (_jsxs("div", Object.assign({ className: 'filter-container' }, { children: [_jsx("h6", Object.assign({ className: 'title' }, { children: translations.product_gallery.popular_products })), _jsx("div", Object.assign({ className: clsx('filter-items'), style: {
                                '--filter-items-column-count': columns,
                            } }, { children: popular_products.sort(SortByLocaleCompare((item) => item.title)).map((item) => (_jsx(StenbolagetTooltip, Object.assign({ title: item.title, placement: 'top' }, { children: _jsxs("div", Object.assign({ onClick: () => onItemChange(item.id), className: clsx('item', {
                                        active: item.id === (selected_item === null || selected_item === void 0 ? void 0 : selected_item.id),
                                    }) }, { children: [_jsx("div", { className: 'item--image image-cover cover-loaded aspect-ratio--1-1 lazy', "data-background": item.thumbnail }), _jsx("div", Object.assign({ className: 'item--title' }, { children: item.title }))] }), item.id) }), item.id))) }))] })))] })) })));
}
